import { useCallback, useEffect, useState } from "react";
import { BarChart3, Loader2, Plus, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Chip, Empty, Err, Panel } from "@/components/community/kit";

export const Polls = ({ community }) => {
  const cid = community.id;
  const [data, setData] = useState(null);
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState({ question: "", options: ["", ""], multi: false });
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/communities/${cid}/polls`);
      setData(res);
    } catch (e) { setError(errMsg(e)); }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, key) => {
    setBusy(key); setError("");
    try { await fn(); await load(); } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  if (!data) return null;

  return (
    <Panel testId="community-polls" title="Quick polls"
      sub="A one-question read of the room — no ballots, no quorum."
      action={data.can_create && (
        <Button data-testid="poll-compose-toggle" size="sm" variant="outline"
          onClick={() => setComposing((v) => !v)}>
          {composing ? "Cancel" : <><BarChart3 className="mr-1.5 h-3.5 w-3.5" /> New poll</>}
        </Button>
      )}>
      <Err testId="polls-error">{error}</Err>

      {composing && (
        <div className="mb-4 space-y-3 rounded-xl border border-ink-600 bg-ink-900/60 p-4">
          <Input data-testid="poll-question" placeholder="Should we meet on a Sunday instead?"
            value={draft.question}
            onChange={(e) => setDraft({ ...draft, question: e.target.value })} />
          {draft.options.map((o, i) => (
            <Input key={i} data-testid={`poll-option-${i}`} placeholder={`Answer ${i + 1}`} value={o}
              onChange={(e) => {
                const options = [...draft.options];
                options[i] = e.target.value;
                setDraft({ ...draft, options });
              }} />
          ))}
          <div className="flex flex-wrap items-center gap-3">
            <Button data-testid="poll-option-add" size="sm" variant="ghost"
              disabled={draft.options.length >= 8}
              onClick={() => setDraft({ ...draft, options: [...draft.options, ""] })}>
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Another answer
            </Button>
            <label className="flex items-center gap-2 text-sm text-slate-400">
              <input data-testid="poll-multi" type="checkbox" className="h-4 w-4" checked={draft.multi}
                onChange={(e) => setDraft({ ...draft, multi: e.target.checked })} />
              Members can pick more than one
            </label>
          </div>
          <Button data-testid="poll-create"
            disabled={draft.question.trim().length < 3
              || draft.options.filter((o) => o.trim()).length < 2 || busy === "create"}
            onClick={() => run(async () => {
              await api.post(`/communities/${cid}/polls`, {
                ...draft, options: draft.options.filter((o) => o.trim()),
              });
              setDraft({ question: "", options: ["", ""], multi: false });
              setComposing(false);
            }, "create")}>
            {busy === "create" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Ask the members
          </Button>
        </div>
      )}

      {data.polls.length === 0 ? (
        <Empty testId="polls-empty">No polls running.</Empty>
      ) : (
        <ul className="space-y-3">
          {data.polls.map((p) => (
            <li key={p.id} data-testid={`poll-${p.id}`}
              className="rounded-xl border border-ink-600 bg-ink-900/60 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-100">{p.question}</p>
                  <p className="mt-1 flex items-center gap-2">
                    <Chip>{p.total} votes</Chip>
                    {!p.open && <Chip className="border-amber-500/40 bg-amber-500/10 text-amber-300">closed</Chip>}
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      {p.author}
                    </span>
                  </p>
                </div>
                <Button data-testid={`poll-delete-${p.id}`} size="sm" variant="ghost"
                  className="text-slate-400 hover:text-red-300"
                  onClick={() => run(async () => {
                    await api.delete(`/communities/${cid}/polls/${p.id}`);
                  }, `del-${p.id}`)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <ul className="mt-3 space-y-2">
                {p.options.map((o) => {
                  const share = p.total ? Math.round((o.votes / p.total) * 100) : 0;
                  const chosen = (p.my_vote || []).includes(o.index);
                  return (
                    <li key={o.index}>
                      <button data-testid={`poll-vote-${p.id}-${o.index}`} disabled={!p.open}
                        onClick={() => run(async () => {
                          await api.post(`/communities/${cid}/polls/${p.id}/vote`,
                            { options: [o.index] });
                        }, `${p.id}-${o.index}`)}
                        className={`relative w-full overflow-hidden rounded-lg border px-3 py-2 text-left text-sm transition-colors disabled:opacity-70 ${
                          chosen ? "border-gold/40 text-gold" : "border-ink-600 text-slate-300 hover:border-gold/30"}`}>
                        <span className="absolute inset-y-0 left-0 bg-gold/10" style={{ width: `${share}%` }} />
                        <span className="relative flex items-center justify-between gap-3">
                          <span className="truncate">{o.text}</span>
                          <span className="font-mono text-[11px] text-slate-500">{share}%</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};
